/**
 * Historie van één verlofaanvraag (Epic 2 — Verlof correctie, Story 2.10,
 * FR-16/NFR-1, UX C3).
 *
 * Bewust een eigen component en niet een blok JSX in `MijnVerlofPage`: de
 * leidinggevende (goedkeuringslijst) en HR (maandafsluiting, epic 4) tonen
 * exact dezelfde regels. Eén weergave, één set woorden — anders drijven de
 * drie schermen uit elkaar en zegt de historie op de ene plek iets anders dan
 * op de andere.
 *
 * Toegankelijkheid (AC-8, UX-DR15): de regels staan in een `<ul>` met een
 * toegankelijke naam, niet als reeks vetgedrukte regels. De uitklapknop zelf
 * hoort bij de aanroeper (die weet waar hij in de rij past); wat hier staat
 * is de inhoud.
 *
 * Microcopy (AC-7): de gebeurtenisnamen komen van de server (`entry.label`),
 * zodat ze op één plek vastliggen. De woorden *verzoek*, *record* en
 * *synchroniseren* komen hier niet voor — het heet *loonadministratie*,
 * *wijziging* en *doorzetten*.
 */

import type { LeaveHistoryEntry } from '@functieloonhuis/shared';
import { useLeaveHistory } from './hooks/useLeaveHistory';
import { formatNlUren } from './urenFormat';

/** "4 aug 09:12" — Amsterdamse tijd, zoals het UX-voorbeeld (C3). */
function formatMoment(iso: string): string {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return '';
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Amsterdam',
  }).format(datum);
}

/**
 * De waardekolom uit C3: `2 uur` bij een gewone gebeurtenis, `2 → 1,25 uur`
 * bij een aanpassing. Draagt de aanvraag tijden (kort verlof), dan staan die
 * erbij — anders is "0,75 uur minder" niet te plaatsen.
 */
function waardeTekst(entry: LeaveHistoryEntry): string | null {
  const nieuw = entry.nieuweUren;
  const oud = entry.oudeUren;
  if (nieuw === null && oud === null) return null;

  const tijden = (start: string | null, eind: string | null) =>
    start && eind ? `${start}–${eind} ` : '';

  if (oud !== null && nieuw !== null && oud !== nieuw) {
    return `${tijden(entry.oudeStartTijd, entry.oudeEindTijd)}${formatNlUren(oud)} → ${tijden(
      entry.nieuweStartTijd,
      entry.nieuweEindTijd
    )}${formatNlUren(nieuw)} uur`;
  }

  const waarde = nieuw ?? oud;
  if (waarde === null) return null;
  return `${tijden(entry.nieuweStartTijd, entry.nieuweEindTijd)}${formatNlUren(waarde)} uur`;
}

/**
 * Story 7.4, AC-7: de oude en de nieuwe periode van een aanpassing door HR,
 * als TEKST. Zonder deze regel draagt een `periode_aangepast`-regel wel een
 * label maar geen perioden, en is hij voor de lezer betekenisloos.
 *
 * De datums komen als kale `YYYY-MM-DD` binnen: geen tijdzone in het spel, dus
 * hier ook geen `new Date()` die er per ongeluk een dag af haalt.
 */
function periodeTekst(entry: LeaveHistoryEntry): string | null {
  const { oudeStartDatum, oudeEindDatum, nieuweStartDatum, nieuweEindDatum } = entry;
  if (!oudeStartDatum || !oudeEindDatum || !nieuweStartDatum || !nieuweEindDatum) return null;
  return `van ${formatKorteDatum(oudeStartDatum)} t/m ${formatKorteDatum(
    oudeEindDatum
  )} naar ${formatKorteDatum(nieuweStartDatum)} t/m ${formatKorteDatum(nieuweEindDatum)}`;
}

const MAANDEN_KORT = [
  'jan',
  'feb',
  'mrt',
  'apr',
  'mei',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'dec',
];

/** "29 jan" — de vorm van §10.6. */
function formatKorteDatum(isoDatum: string): string {
  const [, maand, dag] = isoDatum.split('-');
  if (!maand || !dag) return isoDatum;
  return `${Number(dag)} ${MAANDEN_KORT[Number(maand) - 1] ?? ''}`;
}

const MAANDEN_VOLUIT = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
];

/** "12 augustus" — Amsterdamse dag, zoals §10.5 de datum schrijft. */
function datumVoluit(iso: string): string {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return '';
  const delen = new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'numeric',
    timeZone: 'Europe/Amsterdam',
  })
    .format(datum)
    .split('-');
  const [dag, maand] = delen;
  if (!dag || !maand) return '';
  return `${Number(dag)} ${MAANDEN_VOLUIT[Number(maand) - 1] ?? ''}`;
}

/** Story 7.8, §10.6 — de vaste aanhef van een periode-aanpassing door HR. */
const PERIODE_AANHEF = 'HR paste de periode aan';

/**
 * De regeltekst van één historieregel, als ÉÉN aaneengesloten zin waar §10.5 of
 * §10.6 er een voorschrijft (KE-7).
 *
 * De server stuurt bij de terugname (story 7.7) en de periode-aanpassing
 * (story 7.8) al een volledig samengestelde zin mee. Doet hij dat niet — een
 * oudere aanroeper, of een testopstelling die alleen de losse delen levert —
 * dan wordt diezelfde zin hier uit dezelfde delen opgebouwd. Nooit een derde
 * formulering: de aanhef en de vorm staan vast, alleen naam, datum en perioden
 * worden ingevuld.
 */
function regelTekst(entry: LeaveHistoryEntry, periode: string | null): string {
  if (entry.soort === 'goedkeuring_teruggenomen') {
    if (entry.label.trim().endsWith('.')) return entry.label;
    return `${entry.label} door ${entry.wieNaam ?? ''} op ${datumVoluit(entry.wanneer)}.`;
  }
  if (entry.soort === 'periode_aangepast') {
    if (entry.label.startsWith(PERIODE_AANHEF)) return entry.label;
    return periode ? `${PERIODE_AANHEF} ${periode}` : entry.label;
  }
  return entry.label;
}

export function LeaveHistoryEntryRow({ entry }: { entry: LeaveHistoryEntry }) {
  const waarde = waardeTekst(entry);
  const periode = periodeTekst(entry);
  // De twee zinsoorten hierboven dragen hun perioden al ín de zin; een tweede
  // span ernaast zou ze een tweede keer tonen.
  const eigenPeriodeSpan = entry.soort !== 'periode_aangepast' ? periode : null;

  return (
    <li data-testid="leave-history-entry" className="px-4 py-1.5 text-xs text-[#1E293B]">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <span className="font-medium min-w-[7rem]">{regelTekst(entry, periode)}</span>
        {eigenPeriodeSpan && <span>{eigenPeriodeSpan}</span>}
        {waarde && <span className="min-w-[7rem]">{waarde}</span>}
        {/* AC-2: een mislukte doorzetting draagt de reden, en verdwijnt dus
            niet omdat hij niet geslaagd is. */}
        {entry.uitkomst && (
          <span className={entry.gelukt === false ? 'text-[#D64545]' : 'text-gray-500'}>
            {entry.uitkomst}
          </span>
        )}
        <span className="text-gray-500">{entry.wieNaam ?? ''}</span>
        <span className="text-gray-400 ml-auto">{formatMoment(entry.wanneer)}</span>
      </div>
      {entry.toelichting && (
        <div className="text-gray-500 italic mt-0.5">&ldquo;{entry.toelichting}&rdquo;</div>
      )}
    </li>
  );
}

export function LeaveHistoryList({
  requestId,
  isOpen,
  naam = 'Historie van dit verlof',
}: {
  requestId: number;
  /** Alleen ophalen wanneer de regel daadwerkelijk uitgeklapt is. */
  isOpen: boolean;
  /** Toegankelijke naam van de lijst (AC-8). */
  naam?: string;
}) {
  const { data, isLoading, isError } = useLeaveHistory(requestId, isOpen);
  const entries = data?.data ?? [];

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <p
        data-testid={`leave-history-loading-${requestId}`}
        role="status"
        className="px-4 py-2 text-xs text-gray-500"
      >
        Historie laden...
      </p>
    );
  }

  if (isError) {
    return (
      <p
        data-testid={`leave-history-error-${requestId}`}
        role="alert"
        className="px-4 py-2 text-xs text-[#D64545]"
      >
        De historie kon niet opgehaald worden.
      </p>
    );
  }

  if (entries.length === 0) {
    return (
      <p
        data-testid={`leave-history-empty-${requestId}`}
        className="px-4 py-2 text-xs text-gray-500"
      >
        Er is nog niets gebeurd met dit verlof.
      </p>
    );
  }

  return (
    // Story 7.4, AC-13 (KE-3): de historie is bereikbaar als `region` met een
    // toegankelijke naam — niet als een reeks vetgedrukte regels. De `<ul>`
    // eronder houdt zijn eigen naam, zodat de bestaande weergave onveranderd
    // blijft en er alleen een landmark omheen komt.
    <section
      role="region"
      aria-label={naam}
      data-testid={`leave-history-region-${requestId}`}
    >
      <ul
        data-testid={`leave-history-list-${requestId}`}
        aria-label={naam}
        className="divide-y divide-[#E2E8F0] bg-gray-50 rounded-md py-1"
      >
        {entries.map((entry, index) => (
          <LeaveHistoryEntryRow key={`${entry.soort}-${entry.wanneer}-${index}`} entry={entry} />
        ))}
      </ul>
    </section>
  );
}
